import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, User, Loader2, Mail, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useHasAllAccess } from "@/hooks/useHasAllAccess";
import { cn } from "@/lib/utils";

interface PurchaseChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "subscription" disables "for me" if the user already has an active All-Access sub. */
  productType: "subscription" | "video";
  productName?: string;
  loading?: boolean;
  /** Called when user confirms. recipientEmail is undefined for "for me", set for gift. */
  onConfirm: (mode: "self" | "gift", recipientEmail?: string) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PurchaseChoiceDialog = ({
  open,
  onOpenChange,
  productType,
  productName,
  loading,
  onConfirm,
}: PurchaseChoiceDialogProps) => {
  const { t } = useTranslation("landing");
  const { user } = useAuth();
  const { data: hasAllAccess } = useHasAllAccess(user?.id);

  const selfBlocked = productType === "subscription" && !!hasAllAccess;

  const [mode, setMode] = useState<"self" | "gift">(selfBlocked ? "gift" : "self");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setMode(selfBlocked ? "gift" : "self");
      setRecipientEmail("");
      setError("");
    }
  }, [open, selfBlocked]);

  const handleConfirm = () => {
    if (mode === "self") {
      onConfirm("self");
      return;
    }
    const email = recipientEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setError(t("gift.invalid_email"));
      return;
    }
    if (user?.email && email === user.email.toLowerCase()) {
      setError(t("gift.self_email_error"));
      return;
    }
    onConfirm("gift", email);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#fcb424]" />
            {t("gift.title")}
          </DialogTitle>
          <DialogDescription>
            {productName
              ? t("gift.desc_with_name", { name: productName })
              : t("gift.desc_generic")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-2">
          {/* For me */}
          <button
            type="button"
            disabled={selfBlocked || loading}
            onClick={() => setMode("self")}
            className={cn(
              "relative rounded-xl border-2 p-4 text-left transition-all",
              selfBlocked
                ? "opacity-50 cursor-not-allowed border-border bg-muted/30"
                : mode === "self"
                ? "border-[#fcb424] bg-[#fcb424]/10 shadow-md"
                : "border-border hover:border-[#fcb424]/50"
            )}
          >
            <User className="h-6 w-6 mb-2 text-[#070e1e]" />
            <div className="font-semibold text-sm">{t("gift.for_me")}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{t("gift.for_me_desc")}</div>
            {selfBlocked && (
              <div className="mt-2 flex items-start gap-1 text-[10px] text-amber-700">
                <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                <span>{t("gift.for_me_blocked")}</span>
              </div>
            )}
          </button>

          {/* Gift */}
          <button
            type="button"
            disabled={loading}
            onClick={() => setMode("gift")}
            className={cn(
              "relative rounded-xl border-2 p-4 text-left transition-all",
              mode === "gift"
                ? "border-[#fcb424] bg-[#fcb424]/10 shadow-md"
                : "border-border hover:border-[#fcb424]/50"
            )}
          >
            <Gift className="h-6 w-6 mb-2 text-[#070e1e]" />
            <div className="font-semibold text-sm">{t("gift.as_gift")}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{t("gift.as_gift_desc")}</div>
          </button>
        </div>

        {mode === "gift" && (
          <div className="space-y-2 pt-2">
            <Label htmlFor="gift-email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {t("gift.recipient_email")}
            </Label>
            <Input
              id="gift-email"
              type="email"
              placeholder={t("gift.recipient_placeholder")}
              value={recipientEmail}
              onChange={(e) => {
                setRecipientEmail(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              disabled={loading}
              autoFocus
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
            <p className="text-[11px] text-muted-foreground">{t("gift.info")}</p>
          </div>
        )}

        <Button
          onClick={handleConfirm}
          disabled={loading}
          className="w-full bg-[#fcb424] text-[#070e1e] hover:bg-[#fcb424]/90 font-semibold mt-2"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t("gift.starting")}
            </>
          ) : mode === "gift" ? (
            <>
              <Gift className="h-4 w-4 mr-2" />
              {t("gift.continue_gift")}
            </>
          ) : (
            t("gift.continue_self")
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseChoiceDialog;
